import React, { FC, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Button, CloseButton, Form } from 'react-bootstrap';
import ModalComponent from '../../../../../components/modal/ModalComponent';
import { FormModel } from '../../../../../models/form/form';
import { Condition, Rule, Target } from '../../../../../models/form/rule';
import { FieldType } from '../../../../../models/form/fieldType';
import { RulesTemplates } from './RulesTemplates';
import Conditions from './Conditions';
import Targets from './Targets';
import Feedback from '../../../../../components/library/inputs/feedback/Feedback';
import { FieldModel } from '../../../../../models/form/field';
import TextInput from '../../../../../components/library/inputs/text/TextInput';
import { addErrorToast } from '../../../../../components/errors/toast/ToastUtils';
import { useDispatch, useSelector } from 'react-redux';
import { setRules } from '../../../../../slices/formadmin';
import { RootState } from '../../../../../store';
import { StepModel } from '../../../../../models/form/step';
import { SectionModel } from '../../../../../models/form/section';
import { Option } from '../../../../../models/form/option';
import { PaymentOption } from '../../../../../models/form/paymentOption';
import { getText } from '../../../../../utilities/CultureUtilities';

export interface FieldTransformed {
  sectionMultipleId: string | undefined;
  fieldId: string;
  name: string;
  type: FieldType;
}

export const verifyFieldsStep = (step: StepModel, rules: Rule[]): boolean => {
  for (const section of step.sections) {
    if (verifyFieldsSection(section, rules)) {
      return true;
    }
  }
  return false;
};

export const verifyFieldsSection = (section: SectionModel, rules: Rule[]): boolean => {
  for (const field of section.fields) {
    for (const rule of rules) {
      for (const orConditions of rule.conditions) {
        for (const andCondition of orConditions) {
          if (andCondition.fieldId === field.id) {
            return true;
          }
        }
      }
      for (const target of rule.targets) {
        if (target.fieldId === field.id) {
          return true;
        }
      }
    }
  }
  return false;
};

const extractFields = (form: FormModel): FieldTransformed[] => {
  const fields: FieldTransformed[] = [];
  for (const step of form.content.steps) {
    for (const section of step.sections) {
      for (const field of section.fields) {
        fields.push({
          sectionMultipleId: section.multiple ? section.id : undefined,
          fieldId: field.id,
          name: field.text
            ? step.id + '.' + section.id + '.' + field.text
            : step.id + '.' + section.id + '.' + field.id,
          type: field.type,
        });
      }
    }
  }
  return fields;
};

const getFormFields = (form: FormModel): FieldModel[] => {
  const fields: FieldModel[] = [];
  for (const step of form.content.steps) {
    for (const section of step.sections) {
      for (const field of section.fields) {
        fields.push(field);
      }
    }
  }
  return fields;
};

const Rules: FC = () => {
  const intl = useIntl();
  const form = useSelector((state: RootState) => state.formAdmin.form);
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage);
  const dispatch = useDispatch();
  const [currentRules, setCurrentRules] = useState(() => _.cloneDeep(form?.content.rules));
  const [fields, setFields] = useState<FieldTransformed[]>([]);
  const [formFields, setFormFields] = useState<FieldModel[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);

  if (currentRules) {
    const removeRule = (ruleId: string) => {
      const rulesTemp = [...currentRules];
      _.remove(rulesTemp, { id: ruleId });
      setCurrentRules(rulesTemp);
    };

    const getField = (fieldId: string): FieldTransformed | undefined => {
      for (const field of fields) {
        if (field.fieldId === fieldId) {
          return field;
        }
      }
      addErrorToast({ message: intl.formatMessage({ id: 'errors.genericRefresh' }) });
      return undefined;
    };

    const getOptions = (fieldId: string): Option[] | PaymentOption[] | undefined => {
      for (const field of formFields) {
        if (field.id === fieldId) {
          if (
            field.type == FieldType.OptionList ||
            field.type == FieldType.PaymentOptionList ||
            field.type == FieldType.Select
          ) {
            return field.options;
          } else if (field.type == FieldType.PaymentChoiceList) {
            return field.choices;
          } else if (field.type == FieldType.YesNo) {
            const options: Option[] = [
              {
                text: 'public.controls.yesNo.yes',
                value: 'yes',
              },
              {
                text: 'public.controls.yesNo.no',
                value: 'no',
              },
            ];
            if (field.allowNa) {
              options.push({ text: 'public.controls.yesNo.na', value: 'na' });
            }
            return options;
          }
        }
      }
      return undefined;
    };

    const createNewCondition = (): Condition => {
      return {
        id: uuidv4(),
        fieldId: fields[0].fieldId,
        comparisonOperator: RulesTemplates[fields[0].type].operators[0],
      };
    };

    const createNewTarget = (): Target => {
      return {
        id: uuidv4(),
        fieldId: fields[0].fieldId,
        action: RulesTemplates[fields[0].type].actions[0],
      };
    };

    const onChangeRuleName = (ruleIndex: number, value: string): void => {
      const rulesTemp = [...currentRules];
      rulesTemp[ruleIndex].name = value;
      setCurrentRules(rulesTemp);
    };

    const addRule = () => {
      const rulesTemp = [...currentRules];
      const newRule: Rule = {
        name: '',
        conditions: [],
        targets: [],
        id: uuidv4(),
      };
      const newCondition = createNewCondition();
      newRule.conditions.push([newCondition]);
      rulesTemp.push(newRule);
      setCurrentRules(rulesTemp);
    };

    const verifyRule = (rule: Rule, newErrors: Record<string, string>) => {
      for (const orConditions of rule.conditions) {
        for (const andCondition of orConditions) {
          const field = getField(andCondition.fieldId);
          if (field) {
            if (field.sectionMultipleId && !rule.sectionMultipleId) {
              rule.sectionMultipleId = field.sectionMultipleId;
            } else if (field.sectionMultipleId && rule.sectionMultipleId !== field.sectionMultipleId) {
              newErrors[rule.id] = 'rulesAdmin.errors.validation1';
              return;
            }
          }
        }
      }
      for (const target of rule.targets) {
        const field = getField(target.fieldId);
        if (field) {
          if (field.sectionMultipleId && !rule.sectionMultipleId) {
            rule.sectionMultipleId = field.sectionMultipleId;
          } else if (field.sectionMultipleId && rule.sectionMultipleId !== field.sectionMultipleId) {
            newErrors[rule.id] = 'rulesAdmin.errors.validation2';
            return;
          } else if (!field.sectionMultipleId && rule.sectionMultipleId) {
            newErrors[rule.id] = 'rulesAdmin.errors.validation3';
            return;
          }
        }
      }
    };

    const verifyRules = () => {
      const rulesTemp = [...currentRules];
      const newErrors = {};
      for (const rule of rulesTemp) {
        rule.sectionMultipleId = undefined;
        verifyRule(rule, newErrors);
      }
      setErrors(newErrors);
      setCurrentRules(rulesTemp);
    };

    const isFieldInRule = (field: FieldModel, renderAllFields: boolean) => {
      if (!renderAllFields) {
        for (const rule of currentRules) {
          for (const condition of rule.conditions) {
            for (const fields of condition) {
              if (fields.fieldId == field.id) {
                return true;
              }
            }
          }
        }
      }
      return false;
    };

    const renderFieldOptions = (
      shouldRenderField: (field: FieldModel) => boolean,
      renderAllFields: boolean,
    ): React.ReactNode => {
      return (
        <React.Fragment>
          {form?.content.steps.map((step) => {
            return (
              <React.Fragment key={step.id}>
                <optgroup className="step-option-group" label={getText(step.name, selectedLanguage)} />
                {step.sections.map((section) => {
                  return (
                    <optgroup
                      className="section-option-group"
                      key={section.id}
                      label={getText(section.name, selectedLanguage)}
                    >
                      {section.fields.map((field, fieldIndex) => {
                        if (shouldRenderField(field) && !isFieldInRule(field, renderAllFields)) {
                          return (
                            <option key={field.id} value={field.id}>
                              {field.text
                                ? field.text
                                : intl.formatMessage({ id: 'toolbox.fields.' + field.type }) +
                                  ' ' +
                                  (fieldIndex + 1) +
                                  ' (' +
                                  intl.formatMessage({ id: 'rulesAdmin.general.untitled' }) +
                                  ')'}
                            </option>
                          );
                        }
                        return null;
                      })}
                    </optgroup>
                  );
                })}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      );
    };

    const renderRules = () => {
      return (
        <div>
          {currentRules.map((rule, ruleIndex) => {
            return (
              <div className="p-3" key={'rule' + ruleIndex}>
                <div
                  style={{
                    border: errors[rule.id] ? '1px solid red' : '1px solid lightgray',
                    padding: 16,
                    borderRadius: 8,
                  }}
                >
                  <div style={{ marginTop: -4 }}>
                    <CloseButton style={{ color: 'white' }} onClick={() => removeRule(rule.id)} />
                  </div>
                  <div>
                    <Form.Label style={{ fontWeight: 'bold' }}>
                      {intl.formatMessage({ id: 'rulesAdmin.general.ruleName' })}
                    </Form.Label>
                  </div>
                  <div>
                    <TextInput value={rule.name} onChange={(e) => onChangeRuleName(ruleIndex, e.target.value)} />
                  </div>
                  <div>
                    <Form.Label style={{ fontWeight: 'bold', marginTop: 16 }}>
                      {intl.formatMessage({ id: 'rulesAdmin.general.conditions' })}
                    </Form.Label>
                  </div>
                  <div>
                    <Conditions
                      renderFieldOptions={renderFieldOptions}
                      getField={getField}
                      getOptions={getOptions}
                      formFields={formFields}
                      rule={rule}
                      setRule={(rule) => {
                        const rulesTemp = [...currentRules];
                        rulesTemp[ruleIndex] = rule;
                        setCurrentRules(rulesTemp);
                        verifyRules();
                      }}
                      createNewCondition={createNewCondition}
                    />
                  </div>
                  <div>
                    <Form.Label style={{ fontWeight: 'bold' }}>
                      {intl.formatMessage({ id: 'rulesAdmin.general.targets' })}
                    </Form.Label>
                  </div>
                  <div>
                    <Targets
                      renderFieldOptions={renderFieldOptions}
                      createNewTarget={createNewTarget}
                      getField={getField}
                      rule={rule}
                      setRule={(rule) => {
                        const rulesTemp = [...currentRules];
                        rulesTemp[ruleIndex] = rule;
                        setCurrentRules(rulesTemp);
                        verifyRules();
                      }}
                    />
                  </div>
                </div>
                <div>
                  {errors[rule.id] && (
                    <Feedback touched={true} error={intl.formatMessage({ id: errors[rule.id] })}></Feedback>
                  )}
                </div>
              </div>
            );
          })}
          <div>
            <Button style={{ height: 38 }} onClick={() => addRule()}>
              {intl.formatMessage({ id: 'rulesAdmin.general.addNewRule' })}
            </Button>
          </div>
        </div>
      );
    };

    const onClose = () => {
      setOpen(false);
      setErrors({});
      setCurrentRules(_.cloneDeep(form?.content.rules));
    };

    const onConfirm = () => {
      if (Object.keys(errors).length > 0) {
        addErrorToast({ message: intl.formatMessage({ id: 'rulesAdmin.errors.onSave' }) });
      } else {
        dispatch(setRules(_.cloneDeep(currentRules)));
        setOpen(false);
      }
    };

    if (!fields) {
      return null;
    }

    return (
      <React.Fragment>
        <Button
          className="rules-btn"
          variant="secondary"
          onClick={() => {
            if (form) {
              setFields(extractFields(_.cloneDeep(form)));
              setFormFields(getFormFields(form));
            }
            setOpen(true);
          }}
        >
          <span className="mdi mdi mdi-function-variant"></span>{' '}
          {intl.formatMessage({ id: 'rulesAdmin.general.rules' })}
        </Button>
        <ModalComponent
          closeSettings={onClose}
          buttons={[
            {
              text: intl.formatMessage({ id: 'rulesAdmin.general.cancel' }),
              onClick: onClose,
              buttonVariant: 'secondary',
            },
            {
              text: intl.formatMessage({ id: 'rulesAdmin.general.save' }),
              onClick: onConfirm,
              buttonVariant: 'primary',
            },
          ]}
          show={open}
          modalSize="lg"
          modalHeading={intl.formatMessage({ id: 'rulesAdmin.general.title' })}
        >
          {fields.length < 2 && <div>{intl.formatMessage({ id: 'rulesAdmin.general.twoFields' })}</div>}
          {fields.length >= 2 && renderRules()}
        </ModalComponent>
      </React.Fragment>
    );
  }
  return null;
};

export default Rules;
